import { Component, OnInit, Inject, ElementRef, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslationsProviderService } from 'src/app/services/translations-provider/translations-provider.service';
import { DynamicFieldsManagerService } from 'src/app/services/dynamic-fields/dynamic-fields-manager.service';
import { findEmptyRequiredFields, markFieldsInvalid, clearInvalidMarkings } from 'src/app/shared/helpers/required-fields';

/**
 * Hosts a single one-to-many section inside a modal dialog.
 *
 * Used by SemOneToManyContainerComponent when the container is configured with
 * EditInDialog. The section itself is the very same <one-to-many-section> that is
 * otherwise rendered inline, so the rendered form and all its bindings are identical -
 * only the placement differs.
 *
 * The section edits the array item in place, exactly as in the inline mode. Discarding
 * the changes is therefore the container's job (it keeps a snapshot); this component
 * only reports whether the user confirmed or cancelled.
 */
@Component({
    selector: 'app-sem-o2m-section-dialog',
    templateUrl: './sem-o2m-section-dialog.component.html',
    styleUrls: ['./sem-o2m-section-dialog.component.css'],
    standalone: false
})
export class SemO2mSectionDialogComponent implements OnInit {

  @ViewChild('sectionBody', { static: true }) sectionBody: ElementRef;

  /** set when Save was pressed with mandatory fields still empty */
  mandatoryError: boolean = false;

  constructor(public dialogRef: MatDialogRef<SemO2mSectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    public tps: TranslationsProviderService,
    public dfms: DynamicFieldsManagerService) {
    // the section is edited in place, so closing by clicking the backdrop or pressing
    // Esc must go through cancel() - otherwise the edits would silently stay applied
    this.dialogRef.disableClose = true;
  }

  ngOnInit() {
    this.dialogRef.updateSize('960px', 'auto');
  }

  /**
   * Second line of the header. Names the record being edited by reusing the very
   * fields the listing shows, so the dialog says "Ukradena gotovina" rather than
   * repeating the container title. Empty for a record that has nothing filled in yet.
   */
  get subtitle(): string {
    let fields = this.dialogData.listingFields;
    let section = this.dialogData.section;

    if (section == null) {
      return "";
    }

    // containers without configured listing fields (Ucinki, Povracila) still have a
    // name on the record itself - it is what the user just typed in
    if (!fields || !fields.length) {
      return section.Title != null ? "" + section.Title : "";
    }

    let parts = [];

    fields.forEach(field => {
      let value = this.dfms.getValue(section, field.Binding);

      if (value == null || typeof value == "undefined") {
        return;
      }

      if (typeof value == "object") {
        value = value.Title != null ? value.Title : null;
      }

      if (value != null && ("" + value).length > 0) {
        parts.push("" + value);
      }
    });

    return parts.slice(0, 2).join(" · ");
  }

  /**
   * The shared message ends with a colon because the main form lists the offending fields
   * after it. Here the fields are marked in place, so the colon is left hanging.
   */
  get mandatoryMessage(): string {
    let text = this.tps.translate('WebGalis_MandatoryFields');
    return text != null ? text.replace(/\s*:\s*$/, "") : text;
  }

  cancel() {
    this.dialogRef.close(false);
  }

  confirm() {
    if (!this.sectionIsValid()) {
      return;
    }

    this.dialogRef.close(true);
  }

  /**
   * Checks the mandatory fields of the section, by the same rules the main form applies
   * when it is saved.
   *
   * The section used to be rendered inside the main form, so the form's own check covered
   * it. In the dialog it sits in an overlay outside the form element, where that check
   * cannot reach it - so it is done here, before the dialog closes.
   */
  private sectionIsValid(): boolean {
    let root = this.sectionBody != null ? this.sectionBody.nativeElement : null;

    if (root == null) {
      return true;
    }

    clearInvalidMarkings(root);

    let empty = findEmptyRequiredFields(root);

    if (empty.length == 0) {
      this.mandatoryError = false;
      return true;
    }

    markFieldsInvalid(empty, () => {
      // the message goes away as soon as the user starts filling the fields in
      this.mandatoryError = false;
    });

    this.mandatoryError = true;
    return false;
  }

}
